import React, { useState, useMemo, useEffect, useContext } from 'react'
import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { VoiceTextField } from '@ifca-root/react-component/src/components/Input/CustomTextField'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import {
  useGetAssetRegistrationQuery,
  useGetCurrentResidualValueInfoLazyQuery,
  useCreateFaResidualValueChangeMutation,
  useUpdateFaResidualValueChangeMutation,
  useGetDocumentDateValidationQuery,
  useLatestOpenPeriodCheckingDateQuery,
  ApprovalStatus,
} from 'generated/graphql'
import * as yup from 'yup'

interface CurrentResidualValueInfo {
  baseOriginalAmount: number
  openingResidualPercent: number
  openingResidualValue: number
  currentResidualPercent: number
  currentResidualValue: number
  currentOriginalValue: number
  currentAccumulatedDepreciation: number
  currentNetBookValue: number
  quantity: number
  currentUsefulLife: number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export const ResidualValueForm = (props: any) => {
  const history = useHistory()
  const location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))

  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [openExitConf, setOpenExitConf] = useState(false)
  const [record, setRecord] = useState(false)
  const [selectedAsset, setSelectedAsset] = useState<any>(
    editData?.Asset || null
  )
  const [
    currentInfo,
    setCurrentInfo,
  ] = useState<CurrentResidualValueInfo | null>(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(SnackBarContext)

  let mode = formMode
  if (formMode === 'resubmit') {
    mode = 'edit'
  }

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const residualValueSchema = yup.object().shape({
    asset_no: yup.mixed().required('Asset is required'),
    change_date: yup.date().required('Change Date is required'),
    new_residual_percent: yup
      .number()
      .typeError('New Residual (%) is required')
      .required('New Residual (%) is required')
      .min(0, 'Residual (%) cannot be negative'),
    new_residual_amount: yup
      .number()
      .typeError('New Residual Amount is required')
      .required('New Residual Amount is required')
      .min(0, 'Residual Amount cannot be negative'),
    description: yup.string().required('Description is required'),
    reason: yup.string().required('Reason is required'),
  })

  const { setValue, control, handleSubmit, watch, errors } = useForm<any>({
    defaultValues: {
      change_date: editData?.change_date || new Date(),
      new_residual_percent: editData?.new_residual_percent || null,
      new_residual_amount: editData?.new_residual_amount || null,
      description: editData?.description || '',
      reason: editData?.reason || '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(residualValueSchema),
  })

  const watchNewResidualPercent = watch('new_residual_percent')
  const watchNewResidualAmount = watch('new_residual_amount')

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: assetsLoading,
    data: { getAssetRegistration } = { getAssetRegistration: [] },
  } = useGetAssetRegistrationQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID: CompanyID,
    },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const [
    getCurrentResidualValueInfo,
    { loading: currentInfoLoading },
  ] = useGetCurrentResidualValueInfoLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: data => {
      if (data?.getCurrentResidualValueInfo) {
        const info = data.getCurrentResidualValueInfo as CurrentResidualValueInfo
        setCurrentInfo(info)
        setValue('old_residual_percent', info.currentResidualPercent)
        setValue('old_residual_amount', info.currentResidualValue)
        setValue('old_netbook_amount', info.currentNetBookValue)
        setValue('original_amount', info.currentOriginalValue)
      }
    },
  })

  const {
    loading: openPeriodCheckDateLoading,
    data: { latestOpenPeriodCheckingDate } = {
      latestOpenPeriodCheckingDate: {} as any,
    },
  } = useLatestOpenPeriodCheckingDateQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const {
    loading: docDateValidationLoading,
    data: { getDocumentDateValidation } = { getDocumentDateValidation: [] },
  } = useGetDocumentDateValidationQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
  })

  const assetsList = useMemo(() => {
    return getAssetRegistration || []
  }, [getAssetRegistration])

  useEffect(() => {
    if (
      (formMode === 'edit' || formMode === 'resubmit') &&
      editData?.fa_asset_id &&
      assetsList.length > 0
    ) {
      const asset = assetsList.find(a => a.fa_asset_id === editData.fa_asset_id)
      if (asset) {
        setSelectedAsset(asset)
        setValue('asset_name', asset.name || '')
        setValue('start_date', asset.start_use_date)
        setValue('model_no', asset.model_no || '')
        getCurrentResidualValueInfo({
          variables: {
            fa_asset_id: asset.fa_asset_id,
            exclude_change_id: editData.residual_value_change_id || null,
          },
        })
      }
    }
  }, [formMode, editData, assetsList])

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createResidualValueChange,
    { loading: createLoading },
  ] = useCreateFaResidualValueChangeMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      history.push(`/fixed-assets/${CompanyID}/assets-change/residual-value`)
    },
  })

  const [
    updateResidualValueChange,
    { loading: updateLoading },
  ] = useUpdateFaResidualValueChangeMutation({
    onError: ({ message }) => {
      const error = message?.substring(15)
      setErrMsg(error)
      setErrorDia(true)
    },
    onCompleted: () => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      history.push(`/fixed-assets/${CompanyID}/assets-change/residual-value`)
    },
  })

  /* -------------------------------------------- */
  /*                   HANDLERS                   */
  /* -------------------------------------------- */
  const calculateNetbookAmount = (
    originalAmount: number,
    residualAmount: number
  ) => {
    return originalAmount - residualAmount
  }

  const onSubmit = (data: any, approvalStatus: ApprovalStatus) => {
    if (approvalStatus === ApprovalStatus.Submit) {
      if (isPeriodClosed) {
        setErrMsg(
          'Cannot submit. The accounting period is closed or does not exist. ' +
            'Please select a date within the open financial period ' +
            (latestOpenPeriodCheckingDate?.StartDate &&
            latestOpenPeriodCheckingDate?.EndDate
              ? `(${latestOpenPeriodCheckingDate.StartDate} to ${latestOpenPeriodCheckingDate.EndDate}).`
              : '.')
        )
        setErrorDia(true)
        return
      }

      if (isDocDateInvalid) {
        setErrMsg(
          'Cannot submit. The change date is outside the allowed validation range. ' +
            'Please select a date within the valid period.'
        )
        setErrorDia(true)
        return
      }
    }

    const currentAsset = selectedAsset || editData?.Asset

    if (!currentAsset) {
      setErrMsg('Please select an asset')
      setErrorDia(true)
      return
    }

    const originalAmount =
      currentInfo?.currentOriginalValue ||
      selectedAsset?.base_original_amount ||
      editData?.Asset?.base_original_amount ||
      0

    const oldResidualAmount = currentInfo?.currentResidualValue || 0
    const oldResidualPercent = currentInfo?.currentResidualPercent || 0

    const newResidualAmount = parseFloat(data.new_residual_amount) || 0
    const newResidualPercent = parseFloat(data.new_residual_percent) || 0

    if (newResidualPercent < 0 || newResidualAmount < 0) {
      setErrMsg('Residual value cannot be negative')
      setErrorDia(true)
      return
    }

    const input = {
      residual_value_change_id: editData?.residual_value_change_id || undefined,
      fa_asset_id: selectedAsset?.fa_asset_id || editData?.fa_asset_id,
      company_id: CompanyID,
      old_residual_percent: oldResidualPercent,
      old_residual_amount: oldResidualAmount,
      new_residual_percent: newResidualPercent,
      new_residual_amount: newResidualAmount,
      old_netbook_amount: calculateNetbookAmount(
        originalAmount,
        oldResidualAmount
      ),
      new_netbook_amount: calculateNetbookAmount(
        originalAmount,
        newResidualAmount
      ),
      change_date: data.change_date,
      description: data.description,
      reason: data.reason,
      ApprovalStatus: approvalStatus,
    }

    if (formMode === 'add') {
      createResidualValueChange({ variables: { input } })
    } else {
      updateResidualValueChange({ variables: { input } })
    }
  }

  const handleAssetChange = (value: any) => {
    if (value) {
      setSelectedAsset(value)
      setValue('asset_no', value)
      setValue('asset_name', value?.name)
      setValue('start_date', value?.start_use_date)
      setValue('model_no', value?.model_no)
      getCurrentResidualValueInfo({
        variables: {
          fa_asset_id: value.fa_asset_id,
          exclude_change_id: editData?.residual_value_change_id || null,
        },
      })
    } else {
      setSelectedAsset(null)
      setValue('asset_no', null)
      setCurrentInfo(null)
      setValue('old_residual_percent', '')
      setValue('old_residual_amount', '')
      setValue('old_netbook_amount', '')
      setValue('original_amount', '')
    }
  }

  const handleResidualPercentChange = (value: number) => {
    const originalAmount =
      currentInfo?.currentOriginalValue ||
      selectedAsset?.base_original_amount ||
      0
    if (originalAmount > 0) {
      const calculatedAmount = (originalAmount * value) / 100
      setValue('new_residual_amount', calculatedAmount)
      setValue(
        'new_netbook_amount',
        calculateNetbookAmount(originalAmount, calculatedAmount)
      )
    }
  }

  const handleResidualAmountChange = (value: number) => {
    const originalAmount =
      currentInfo?.currentOriginalValue ||
      selectedAsset?.base_original_amount ||
      0
    if (originalAmount > 0) {
      const calculatedPercent = (value / originalAmount) * 100
      setValue('new_residual_percent', calculatedPercent)
      setValue(
        'new_netbook_amount',
        calculateNetbookAmount(originalAmount, value)
      )
    }
  }

  /* -------------------------------------------- */
  /*               PERIOD VALIDATION              */
  /* -------------------------------------------- */
  const watchChangeDate =
    watch('change_date') || editData?.change_date || new Date()
  const changeDateStr = (() => {
    const d = new Date(watchChangeDate)
    const year = d.getFullYear()
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const day = d
      .getDate()
      .toString()
      .padStart(2, '0')
    return `${year}-${month}-${day}`
  })()

  const isPeriodClosed =
    latestOpenPeriodCheckingDate?.StartDate &&
    latestOpenPeriodCheckingDate?.EndDate
      ? changeDateStr < latestOpenPeriodCheckingDate?.StartDate ||
        changeDateStr > latestOpenPeriodCheckingDate?.EndDate
      : false

  const today = new Date()
  const monthsBefore = getDocumentDateValidation?.[0]?.MonthsBefore ?? 12
  const monthsAfter = getDocumentDateValidation?.[0]?.MonthsAfter ?? 1

  const minValidDate = new Date(today)
  minValidDate.setMonth(minValidDate.getMonth() - monthsBefore)
  minValidDate.setHours(0, 0, 0, 0)

  const maxValidDate = new Date(today)
  maxValidDate.setMonth(maxValidDate.getMonth() + monthsAfter)
  maxValidDate.setHours(23, 59, 59, 999)

  const changeDate = new Date(watchChangeDate)
  const isDocDateInvalid =
    changeDate.getTime() < minValidDate.getTime() ||
    changeDate.getTime() > maxValidDate.getTime()

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Active)),
    color: 'primary',
    props: { type: 'button' },
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: handleSubmit(data => onSubmit(data, ApprovalStatus.Submit)),
    color: 'primary',
    props: { type: 'button' },
  }

  let footerOptions: any[]
  if (formMode === 'add' || formMode === 'edit' || formMode === 'resubmit') {
    footerOptions = [draftFooterOption, submitFooterOption]
  } else {
    footerOptions = []
  }

  return (
    <>
      {(assetsLoading ||
        createLoading ||
        updateLoading ||
        currentInfoLoading ||
        openPeriodCheckDateLoading ||
        docDateValidationLoading) && <Loading />}

      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/residual-value`
          )
        }}
        smTitle={'Fixed Assets'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Fixed Assets' },
          { name: 'Assets Change' },
          { name: 'Residual Value', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'resubmit'
                ? 'Resubmit'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents
          section={{
            header: { title: 'Asset Information' },
          }}
        >
          <Controller
            render={({ value, onChange }) => {
              const defVal = editData?.Asset || null

              return (
                <Autocomplete
                  options={
                    assetsList?.sort((a, b) => {
                      return a?.DocNo?.localeCompare(b?.DocNo)
                    }) || []
                  }
                  getOptionLabel={option =>
                    `${option?.DocNo} - ${option?.name}`
                  }
                  fullWidth
                  onChange={(event, value: any) => handleAssetChange(value)}
                  defaultValue={defVal}
                  disabled={mode === 'approve-reject'}
                  renderOption={props => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{props?.DocNo}</span>
                          <span className="desc" style={{ marginLeft: '8px' }}>
                            {props?.name}
                          </span>
                        </div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <TextField
                        {...params}
                        label="Asset"
                        style={{ width: '100%' }}
                        required
                        margin="normal"
                        helperText={errors?.asset_no?.message}
                        error={errors?.asset_no ? true : false}
                      />
                    )
                  }}
                />
              )
            }}
            name="asset_no"
            control={control}
            margin="normal"
            defaultValue={editData?.Asset || null}
          />

          <Controller
            as={TextField}
            name="asset_name"
            label="Asset Name"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            defaultValue={editData?.Asset?.name || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Asset Start Date"
                  format="dd/MM/yyyy"
                  readOnly
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    style: { display: 'none' },
                  }}
                  className="left"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                />
              )}
              name="start_date"
              control={control}
              defaultValue={editData?.Asset?.start_use_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            as={TextField}
            name="model_no"
            label="Model No"
            autoComplete="off"
            control={control}
            fullWidth
            margin="dense"
            className="right"
            defaultValue={editData?.Asset?.model_no || 'N/A'}
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentOriginalValue ?? value ?? '0'}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Original Amount"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="original_amount"
            control={control}
            defaultValue={editData?.Asset?.base_original_amount || '0'}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentResidualPercent ?? value ?? '0'}
                thousandSeparator
                decimalScale={4}
                fixedDecimalScale
                label="Current Residual Value (%)"
                fullWidth
                className="left"
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                // helperText={
                //   currentInfo
                //     ? `Opening: ${currentInfo.openingResidualPercent?.toFixed(
                //         4
                //       )}%`
                //     : ''
                // }
              />
            )}
            name="old_residual_percent"
            control={control}
            defaultValue={editData?.old_residual_percent || '0'}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentResidualValue ?? value ?? '0'}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Residual Amount"
                fullWidth
                className="right"
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
                // helperText={
                //   currentInfo
                //     ? `Opening: ${formatCurrency(
                //         currentInfo.openingResidualValue
                //       )}`
                //     : ''
                // }
              />
            )}
            name="old_residual_amount"
            control={control}
            defaultValue={editData?.old_residual_amount || '0'}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={currentInfo?.currentNetBookValue ?? value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="Current Net Book Amount"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="old_netbook_amount"
            control={control}
            defaultValue={editData?.old_netbook_amount || 0}
          />
        </CardContents>

        <CardContents
          section={{
            header: { title: 'Residual Value Adjustment' },
          }}
        >
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              render={({ value, onChange }) => (
                <KeyboardDatePicker
                  value={value}
                  onChange={onChange}
                  label="Change Date"
                  format="dd/MM/yyyy"
                  margin="dense"
                  allowKeyboardControl
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  required
                  disabled={mode === 'approve-reject'}
                />
              )}
              name="change_date"
              control={control}
              defaultValue={editData?.change_date || new Date()}
            />
          </MuiPickersUtilsProvider>

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue)
                  handleResidualPercentChange(floatValue || 0)
                }}
                thousandSeparator
                decimalScale={4}
                fixedDecimalScale
                label="New Residual Value (%)"
                fullWidth
                className="left"
                margin="dense"
                required
                disabled={mode === 'approve-reject'}
                helperText={errors?.new_residual_percent?.message}
                error={errors?.new_residual_percent ? true : false}
              />
            )}
            name="new_residual_percent"
            control={control}
            defaultValue={editData?.new_residual_percent || ''}
          />

          <Controller
            render={({ value, onChange }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                onValueChange={({ floatValue }) => {
                  onChange(floatValue)
                  handleResidualAmountChange(floatValue || 0)
                }}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="New Residual Amount"
                fullWidth
                className="right"
                margin="dense"
                required
                disabled={mode === 'approve-reject'}
                helperText={errors?.new_residual_amount?.message}
                error={errors?.new_residual_amount ? true : false}
              />
            )}
            name="new_residual_amount"
            control={control}
            defaultValue={editData?.new_residual_amount || ''}
          />

          <Controller
            render={({ value }) => (
              <NumberFormat
                customInput={TextField as any}
                value={value}
                thousandSeparator
                decimalScale={2}
                fixedDecimalScale
                label="New Net Book Amount"
                fullWidth
                margin="dense"
                InputProps={{
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            )}
            name="new_netbook_amount"
            control={control}
            defaultValue={editData?.new_netbook_amount || 0}
          />

          <VoiceTextField
            mounted={true}
            label="Description"
            name="description"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.description || '',
              helperText: errors?.description?.message,
              error: errors?.description ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />

          <VoiceTextField
            mounted={true}
            label="Reason"
            name="reason"
            setValue={setValue}
            record={record}
            setRecord={setRecord}
            control={control}
            controllerProps={{
              autoComplete: 'off',
              defaultValue: editData?.reason || '',
              helperText: errors?.reason?.message,
              error: errors?.reason ? true : false,
            }}
            disabled={mode === 'approve-reject'}
            required
          />
        </CardContents>
      </ContentWrapper>

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <ErrorDialog
        errorDia={errorDia}
        setErrorDia={setErrorDia}
        errorMsg={errMsg}
        errorHeaderMsg={'Error'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(
            `/fixed-assets/${CompanyID}/assets-change/residual-value`
          )
        }}
      />
    </>
  )
}
